import { SearchDealsByStageDocument } from '@paintscout/api'
import type { Deal } from '@paintscout/api'
import type { AutomationOption, UpdateDealStageActionConfig } from '@paintscout/util/builder'
import type { Pipeline, PipelineStage } from 'paintscout'

/**
 * Fetches all deals in a given stage (handles pagination)
 */
export async function fetchAllDealsInStage(apolloClient: any, stageKey: string): Promise<Deal[]> {
  let allDeals: Deal[] = []
  let bookmark: string | undefined = undefined
  let hasMore = true

  while (hasMore) {
    const result = await apolloClient.query({
      query: SearchDealsByStageDocument,
      variables: {
        query: '*',
        stage: stageKey,
        bookmark,
        limit: 100 // Fetch in batches of 100
      },
      fetchPolicy: 'network-only'
    })

    const deals = result.data?.searchDealsByStage?.deals || []
    allDeals = [...allDeals, ...deals]
    bookmark = result.data?.searchDealsByStage?.bookmark || undefined
    hasMore = deals.length === 100 && bookmark !== undefined
  }

  return allDeals
}

/**
 * Checks if there are any deals in a given stage
 */
export async function hasDealsInStage(apolloClient: any, stageKey: string): Promise<boolean> {
  try {
    const result = await apolloClient.query({
      query: SearchDealsByStageDocument,
      variables: {
        query: '*',
        stage: stageKey,
        limit: 1
      },
      fetchPolicy: 'network-only'
    })
    return (result.data?.searchDealsByStage?.aggregations?.totalDeals ?? 0) > 0
  } catch (error) {
    console.error('Error checking for deals in stage:', error)
    // If there's an error, assume there are no deals to avoid blocking deletion
    return false
  }
}

/**
 * Moves all deals from one stage to another
 */
export async function moveDealsToStage(
  apolloClient: any,
  updateDealStage: (options: { variables: { deal: any } }) => Promise<any>,
  deals: Deal[],
  targetStage: PipelineStage,
  pipeline: Pipeline
): Promise<void> {
  for (const deal of deals) {
    try {
      await updateDealStage({
        variables: {
          deal: {
            ...deal,
            stage: {
              value: targetStage.key
            },
            pipeline: {
              value: pipeline.key,
              label: pipeline.label
            }
          }
        }
      })
    } catch (error) {
      console.error(`Error moving deal ${deal._id} to stage ${targetStage.key}:`, error)
      // Continue with other deals even if one fails
    }
  }
}

/**
 * Finds automations that use a specific stage
 */
export function findAutomationsUsingStage(
  automations: AutomationOption[],
  pipelineKey: string,
  stageKey: string
): AutomationOption[] {
  return automations.filter((automation) => {
    return automation.actions.some((action) => {
      return (
        action.type === 'update-deal-stage' &&
        (action.config as UpdateDealStageActionConfig)?.targetPipeline?.value === pipelineKey &&
        (action.config as UpdateDealStageActionConfig)?.targetStage?.value === stageKey
      )
    })
  })
}

/**
 * Updates automations to disable them and clear the target stage when a stage is deleted
 */
export function updateAutomationsForDeletedStage(
  automations: AutomationOption[],
  stageInAutomations: AutomationOption[],
  pipelineKey: string,
  deletedStageKey: string
): Record<string, AutomationOption> {
  return automations.reduce(
    (acc, automation) => {
      if (stageInAutomations.some((a) => a.id === automation.id)) {
        const updatedAutomation = {
          ...automation,
          active: false,
          actions: automation.actions.map((action) => {
            if (
              action.type === 'update-deal-stage' &&
              (action.config as UpdateDealStageActionConfig)?.targetPipeline?.value === pipelineKey &&
              (action.config as UpdateDealStageActionConfig)?.targetStage?.value === deletedStageKey
            ) {
              return {
                ...action,
                config: {
                  ...action.config,
                  targetStage: { value: '' }
                }
              }
            }
            return action
          })
        }
        acc[automation.id] = updatedAutomation
      } else {
        acc[automation.id] = automation
      }
      return acc
    },
    {} as Record<string, AutomationOption>
  )
}

/**
 * Gets the first remaining stage in a pipeline (excluding the stage being deleted)
 */
export function getFirstRemainingStage(pipeline: Pipeline, deletedStageKey: string): PipelineStage | null {
  const remainingStages = pipeline.stages.filter((stage) => stage.key !== deletedStageKey)
  return remainingStages.length > 0 ? remainingStages[0] : null
}

/**
 * Handles moving all deals from a deleted stage to the first remaining stage
 */
export async function handleMoveDealsOnStageDeletion(
  apolloClient: any,
  updateDealStage: (options: { variables: { deal: any } }) => Promise<any>,
  pipeline: Pipeline,
  deletedStageKey: string
): Promise<void> {
  const firstStage = getFirstRemainingStage(pipeline, deletedStageKey)
  if (!firstStage) {
    console.error('Cannot delete stage: no other stages available')
    throw new Error('Cannot delete stage: no other stages available')
  }

  try {
    const allDeals = await fetchAllDealsInStage(apolloClient, deletedStageKey)
    await moveDealsToStage(apolloClient, updateDealStage, allDeals, firstStage, pipeline)
  } catch (error) {
    console.error('Error moving deals to first stage:', error)
    throw error
  }
}
